// import MessageIcon from '@repo/ui/components/icons/MessageIcon';
import NotifictionsIcon from '@repo/ui/components/icons/NotifictionsIcon';
import { Input } from '@repo/ui/components/input';
import SearchIcon from '@repo/ui/components/icons/Search';
import { useAppSelector } from '@/core/global-store';
import Logo from '@repo/ui/components/icons/Logo'; 
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { apiGet } from '@/lib/axios/axios-utils';
import { useDebounce } from '@/core/global-hooks';
import { endpoints } from '@/lib/axios/axios-global';
import MobileSearchResults from './components/MobileSearchResults';
import SearchResultsDropdown from './components/SearchResultsDropdown';

const Header = () => {
    const user = useAppSelector((state) => state.auth.me);
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState<any>(null);
    const [showResults, setShowResults] = useState(false);
    const [loading, setLoading] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notificationLoading, setNotificationLoading] = useState(false);
    const [showMobileSearch, setShowMobileSearch] = useState(false);
    const mobileSearchRef = useRef<HTMLDivElement>(null);

    // Debounce search query to avoid too many API calls
    const debouncedSearchQuery = useDebounce(searchQuery, 500);

    const performGlobalSearch = async (query: string) => {
        if (!query.trim()) {
            setSearchResults(null);
            setShowResults(false);
            return;
        }

        setLoading(true);
        try {
            const response = await apiGet({
                endpoint: '/global-search',
                queryParams: { search: query },
            });

            if (response?.data?.data) {
                setSearchResults(response.data.data);
                setShowResults(true);
            }
        } catch (error) {
            console.error('Search error:', error);
            setSearchResults(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        performGlobalSearch(debouncedSearchQuery);
    }, [debouncedSearchQuery]);

    // Fetch unread notifications count
    const fetchUnreadCount = async () => {
        try {
            setNotificationLoading(true);
            const listRes = await apiGet({
                endpoint: endpoints.notification.list,
                queryParams: { page: 1, per_page: 1000 },
            });

            let unread = 0;
            if (listRes.data.data?.data) {
                const notifications = listRes.data.data.data;
                unread = notifications.filter((n: any) => !n.is_read).length;
            } else if (Array.isArray(listRes.data.data)) {
                const notifications = listRes.data.data;
                unread = notifications.filter((n: any) => !n.is_read).length;
            }
            setUnreadCount(unread);
        } catch (error) {
            console.error('Error fetching notification count:', error);
        } finally {
            setNotificationLoading(false);
        }
    };

    // Fetch unread count on mount and set up interval to refresh
    useEffect(() => {
        fetchUnreadCount();
        // Refresh every 30 seconds
        const interval = setInterval(fetchUnreadCount, 30000);

        // Refresh when page becomes visible
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchUnreadCount();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Close search results when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setShowResults(false);
            }
            if (mobileSearchRef.current && !mobileSearchRef.current.contains(event.target as Node)) {
                setShowMobileSearch(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        performGlobalSearch(searchQuery);
    };

    const handleResultClick = (type: string, item: any) => {
        // Navigate based on the type of result
        switch (type) {
            case 'users':
                navigate(`/human-resources/employee/${item.id}`);
                break;
            case 'customers':
                navigate(`/customer-relations/view-customer/${item.id}`);
                break;
            case 'orders':
                navigate(`/orders/order-list/${item.id}`);
                break;
            case 'products':
                navigate(`/products`);
                break;
            case 'services':
                navigate(`/services`);
                break;
            case 'branches':
                navigate(`/branches`);
                break;
            default:
                break;
        }
        setShowResults(false);
        setSearchQuery('');
    };

    const getTypeLabel = (type: string) => {
        const labels: { [key: string]: string } = {
            users: 'المستخدمين',
            customers: 'العملاء',
            orders: 'الطلبات',
            products: 'المطبوعات',   
            services: 'الخدمات',
            branches: 'الفروع',
        };
        return labels[type] || type;
    };

    return (
        <header className="bg-primery-blue-500 px-8 py-4 flex flex-row justify-between items-center h-16">
            <div className="flex gap-3">
                <div ref={searchRef} className="md:flex hidden flex-col relative">
                    <div className="flex gap-2">
                        {/* Notifications Button */}
                        <div className="flex flex-row items-center gap-4">
                            <button
                                onClick={() => {
                                    navigate('/notifications');
                                    fetchUnreadCount(); // Refresh count when navigating
                                }}
                                className="relative p-2 hover:bg-primery-blue-600 rounded-lg transition-colors"
                                title="الإشعارات"
                            >
                                <NotifictionsIcon width="24" height="24" className="text-white-blue-50" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                )}
                            </button>
                        </div>
                        {/* Search Form */}
                        <form
                            onSubmit={handleSearchSubmit}
                            className="flex flex-row items-center bg-primery-blue-600 md:w-80 lg:w-96 xl:w-[540px] h-[46px] px-4 py-2 rounded-[8px]"
                        >
                            <Input
                                placeholder="ابحث برقم الطلب، اسم العميل، أو نوع الخدمة......"
                                className="text-primery-blue-300 text-xs md:text-xs lg:text-base lg:leading-[30px] border-none outline-none focus-visible:ring-0 placeholder:text-primery-blue-300"
                                value={searchQuery}
                                onChange={handleSearchInputChange}
                            />
                            <button type="submit" className="bg-transparent border-none">
                                <SearchIcon className="text-primery-blue-300" />
                            </button>
                        </form>
                    </div>

                    {/* Search Results Dropdown */}
                    {showResults && searchResults && (
                        <SearchResultsDropdown
                            searchResults={searchResults}
                            loading={loading}
                            getTypeLabel={getTypeLabel}
                            handleResultClick={handleResultClick}
                        />
                    )}
                </div>
            </div>

            {/*  Header (Mobile) logo and notifications button */}
            <div className="md:hidden flex w-full justify-start relative gap-2">
                <div className="flex flex-row items-center gap-4">
                    <button
                        onClick={() => {
                            navigate('/notifications');
                            fetchUnreadCount(); // Refresh count when navigating
                        }}
                        className="relative p-2 hover:bg-primery-blue-600 rounded-lg transition-colors"
                        title="الإشعارات"
                    >
                        <NotifictionsIcon width="26" height="26" className="text-primery-blue-300" />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </button>
                </div>
                <button onClick={() => setShowMobileSearch(true)} className="bg-transparent border-none" title="البحث">
                    <SearchIcon className="text-primery-blue-300" width="24" height="24" />
                </button>

                <span
                    className="cursor-pointer absolute sm:right-[50%] right-[43%] md:top-[-4px] top-[5px] bottom-0"
                    onClick={() => navigate('/')}
                >
                    <Logo width="51" height="32" />
                </span>
            </div>

            {/* Mobile Search Modal */}
            {showMobileSearch && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden">
                    <div ref={mobileSearchRef} className="bg-primery-blue-500 p-4 w-full">
                        <div className="flex items-center gap-2 mb-4">
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSearchSubmit(e);
                                }}
                                className="flex-1 flex flex-row items-center bg-primery-blue-600 h-[46px] px-4 py-2 rounded-[8px]"
                            >
                                <Input
                                    placeholder="ابحث برقم الطلب، اسم العميل، أو نوع الخدمة......"
                                    className="text-primery-blue-300 text-sm border-none outline-none focus-visible:ring-0 placeholder:text-primery-blue-300"
                                    value={searchQuery}
                                    onChange={handleSearchInputChange}
                                    autoFocus
                                />
                                <button type="submit" className="bg-transparent border-none">
                                    <SearchIcon className="text-primery-blue-300" />
                                </button>
                            </form>
                            <button
                                onClick={() => {
                                    setShowMobileSearch(false);
                                    setShowResults(false);
                                    setSearchQuery('');
                                }}
                                className="text-white-blue-50 px-3 py-2"
                            >
                                إلغاء
                            </button>
                        </div>

                        {/* Mobile Search Results */}
                        {showResults && searchResults && (
                            <MobileSearchResults
                                searchResults={searchResults}
                                loading={loading}
                                getTypeLabel={getTypeLabel}
                                handleResultClick={handleResultClick}
                            />
                        )}
                    </div>
                </div>
            )}

            {/*  User Info (Desktop) */}
            <div className="flex flex-row items-center">
                <div className="md:flex hidden flex-row items-center gap-2">
                    {/* <Link to="/settings">
                    //     <img
                    //         src={user?.data.image}
                    //         alt={user?.data.name}
                    //         className="max-w-[45px] max-h-[45px] aspect-square w-full h-full rounded-full"
                    //     />
                    // </Link> */}
                    <div className="text-white-blue-50 xl:pe-11 flex flex-col gap-2 items-start">
                        <p className="font-medium text-sm leading-[14px]">{user?.data?.name}</p>
                        <p className="font-medium text-primery-blue-100 text-xs leading-[13.8px]">
                            {user?.data?.roles?.join(', ') || user?.data?.roles?.[0]}
                        </p>
                    </div>
                </div>
                <span className="cursor-pointer md:block hidden" onClick={() => navigate('/')}>
                    <Logo width="70" height="40" />
                </span>
            </div>
        </header>
    );
};

export default Header;
